import {
  createTheme,
  decomposeColor,
  recomposeColor,
  type Palette,
  type PaletteColor,
  type PaletteOptions,
  type Theme,
} from '@mui/material/styles';

const DARK_BACKGROUND = '#121212';
const DARK_PAPER = '#1e1e1e';
const DARK_DIVIDER = '#757575';

function mixOnDark(color: string, weight: number) {
  const foreground = decomposeColor(color);
  const background = decomposeColor(DARK_BACKGROUND);
  const mix = (channel: number | string, backgroundChannel: number | string) =>
    Math.round(Number(channel) * weight + Number(backgroundChannel) * (1 - weight));

  return recomposeColor({
    type: 'rgb',
    values: [
      mix(foreground.values[0], background.values[0]),
      mix(foreground.values[1], background.values[1]),
      mix(foreground.values[2], background.values[2]),
    ],
  });
}

function withDarkShades(color: PaletteColor) {
  return {
    ...color,
    50: mixOnDark(color.main, 0.08),
    100: mixOnDark(color.main, 0.14),
    200: mixOnDark(color.main, 0.22),
    300: mixOnDark(color.main, 0.3),
    400: mixOnDark(color.main, 0.38),
    500: color.light,
    600: color.light,
    700: color.light,
    800: color.light,
    900: color.light,
    A100: mixOnDark(color.main, 0.16),
    A200: mixOnDark(color.main, 0.24),
    A400: color.light,
    A700: color.light,
  };
}

function createDarkPalette(): PaletteOptions {
  const muiDark = createTheme({ palette: { mode: 'dark' } }).palette;

  return {
    mode: 'dark',
    primary: withDarkShades(muiDark.primary),
    secondary: withDarkShades(muiDark.secondary),
    error: withDarkShades(muiDark.error),
    warning: withDarkShades(muiDark.warning),
    info: withDarkShades(muiDark.info),
    success: withDarkShades(muiDark.success),
    grey: {
      ...muiDark.grey,
      50: DARK_PAPER,
      100: '#2c2c2c',
    },
    common: muiDark.common,
    text: muiDark.text,
    background: {
      default: DARK_BACKGROUND,
      paper: DARK_PAPER,
    },
    divider: DARK_DIVIDER,
    action: muiDark.action,
    gradient: {
      primary: muiDark.primary.main,
      secondary: muiDark.secondary.main,
      warning: muiDark.warning.main,
    } as PaletteOptions['gradient'],
  } as PaletteOptions;
}

export const COLOR_SCHEME_ATTRIBUTE = 'data' as const;
export const COLOR_SCHEME_MODE_STORAGE_KEY = 'mg-color-mode';
export const COLOR_SCHEME_STORAGE_KEY = 'mg-color-scheme';

export type ThemeMode = 'light' | 'dark' | 'system';

function toPaletteOptions(palette: Palette): PaletteOptions {
  const options: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(palette)) {
    if (key === 'mode' || typeof value === 'function') {
      continue;
    }
    options[key] = value;
  }

  return JSON.parse(JSON.stringify(options)) as PaletteOptions;
}

type SchemePalette = { palette: Palette };

export type ColorSchemeTheme = Theme & {
  colorSchemeSelector?: string;
  cssVarPrefix?: string;
  colorSchemes: {
    light?: SchemePalette;
    dark?: SchemePalette;
  };
};

function hasColorSchemes(theme: Theme): boolean {
  const candidate = theme as ColorSchemeTheme;
  return Boolean(candidate.vars && candidate.colorSchemes?.light && candidate.colorSchemes?.dark);
}

type CreateThemeOptions = NonNullable<Parameters<typeof createTheme>[0]>;

export function withColorSchemes(theme: Theme, cssVarPrefix?: string): ColorSchemeTheme {
  if (hasColorSchemes(theme)) {
    return theme as ColorSchemeTheme;
  }

  const options: CreateThemeOptions = {
    cssVariables: {
      colorSchemeSelector: COLOR_SCHEME_ATTRIBUTE,
      ...(cssVarPrefix ? { cssVarPrefix } : {}),
    },
    colorSchemes: {
      light: {
        palette: toPaletteOptions(theme.palette),
      },
      // Standard MUI dark colors, plus the shade keys existing styles already read.
      dark: {
        palette: createDarkPalette(),
      },
    },
    shape: theme.shape,
    shadows: theme.shadows,
    typography: theme.typography,
    components: theme.components,
    breakpoints: { values: theme.breakpoints.values },
    spacing: theme.spacing,
  };

  return createTheme(options) as ColorSchemeTheme;
}
